import { filter, find, reduce, isEmpty, pick } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import { isBusinessEntity } from '../../../../../../model/OrganisationTypes';
import Statuses from '../../../../../../model/Statuses';
import useOrganisationGroups from '../../../../../data/hooks/useOrganisationGroups';
import FormTreeModalSelector from '../../../../controls/NextTree/FormTreeModalSelector';
import RoleAssignmentSelector from '../../../../controls/RoleAssignmentSelector';
import SubtreeValueView from '../../../../controls/ValueViews/SubtreeValueView';
import Spinner from '../../../../utils/Spinner';
import useT from '../../../../utils/Translations/useT';
import useEntityFormContext from '../../internal/useEntityFormContext';
import useAssigmentRoles from './data/useAssigmentRoles';
import EntityField from '../../internal/EntityField';
import Button from '../../../../controls/Button';
const GROUP = 'OrganisationGroup';
export const ORGANISATION = 'Organisation';
export const ORGANISATION_STRUCTURE = 'RoleOrganisationStructure';
export const LEVEL = 'OrgStructureLevel';
export const ROLE = 'RoleXOrgLevel';
const plugins = {
    nodeIsVisible: node => node.status === Statuses.Active.value && node.type !== 'LEVEL_HIDDEN',
};
const RoleAssignmentField = ({ label, required = false, disabled = false, isForceAutoSelect = false, orgGroupId, personEntityTypeId, initialSelect, name = 'roles', ...rest }) => {
    const t = useT();
    const _label = useMemo(() => label || t('Role'), [label, t]);
    const { values, setFieldValue } = useEntityFormContext();
    const { models: organisationGroupSelector, loading: organisationGroupSelectorLoading, } = useOrganisationGroups({ initialSelect });
    const { models: orgGroupRolesSelector, loading: orgGroupRolesSelectorLoading, } = useAssigmentRoles({
        personEntityTypeId,
        orgGroupId,
    });
    const models = useMemo(() => {
        const currentGroup = find(organisationGroupSelector || [], {
            id: orgGroupId,
        });
        return currentGroup
            ? [currentGroup, ...orgGroupRolesSelector]
            : orgGroupRolesSelector;
    }, [organisationGroupSelector, orgGroupRolesSelector, orgGroupId]);
    const selection = useCallback(rolesField => {
        const valuesByComplexKey = reduce(rolesField.input.value || [], (acc, value) => {
            acc[`${value.organisationId}-${value.orgStructureLevelId}-${value.id}`] = true;
            return acc;
        }, {});
        const foundRoles = filter(orgGroupRolesSelector, node => {
            const { __typename, organisationId, orgStructureLevelId, id, } = pick(node, [
                '__typename',
                'organisationId',
                'orgStructureLevelId',
                'id',
            ]);
            return (__typename === ROLE &&
                valuesByComplexKey[`${organisationId}-${orgStructureLevelId}-${id}`]);
        });
        return foundRoles;
    }, [orgGroupRolesSelector]);
    const viewSelector = useCallback(assignedRoleField => {
        let selectedNodes = selection(assignedRoleField);
        if (isEmpty(selectedNodes)) {
            return [];
        }
        let res = [...selectedNodes];
        selectedNodes.map(x => makeTreeSelection(resolveNodeParentId(x)));
        function makeTreeSelection(parentId) {
            while (parentId !== null && !isEmpty(selectedNodes)) {
                selectedNodes = filter(orgGroupRolesSelector, node => resolveNodeId(node) === parentId);
                if (!isEmpty(selectedNodes)) {
                    res = res.concat(selectedNodes);
                    parentId = resolveNodeParentId(selectedNodes);
                }
            }
        }
        return res;
    }, [selection, orgGroupRolesSelector]);
    const onChange = useCallback(assignedRoleField => fieldValues => {
        var _a, _b, _c;
        const _values = fieldValues.filter(x => x.__typename === ROLE);
        assignedRoleField.input.onChange(_values);
        const [first] = _values;
        isEmpty(values.timeZone) &&
            setFieldValue('timeZone', (_a = find(orgGroupRolesSelector, org => isBusinessEntity(org) && org.id === first.organisationId)) === null || _a === void 0 ? void 0 : _a.timeZone);
        isEmpty(values.countryOfBirth) &&
            setFieldValue('countryOfBirth', (_c = (_b = find(orgGroupRolesSelector, org => isBusinessEntity(org) && org.id === first.organisationId)) === null || _b === void 0 ? void 0 : _b.country) === null || _c === void 0 ? void 0 : _c.code);
    }, [values, orgGroupRolesSelector, setFieldValue]);
    const rolesForSelection = useMemo(() => filter(orgGroupRolesSelector, ({ __typename }) => __typename === ROLE), [orgGroupRolesSelector]);
    const forceUpdateFields = useCallback(rolesField => {
        const isRolesSelected = rolesField.input.value;
        if (isEmpty(isRolesSelected) &&
            isForceAutoSelect &&
            rolesForSelection.length === 1) {
            rolesField.input.onChange(rolesForSelection[0].id);
        }
    }, [isForceAutoSelect, rolesForSelection]);
    const selectionFilter = useCallback(values => filter(values, nodeIsLeaf), []);
    const renderNodeAddons = (node, meta) => {
        const { handleClear } = meta;
        const handleClearFn = e => {
            handleClear();
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
        };
        const addons = [];
        if (isGroup(node)) {
            addons.push(<Button additionClasses="text-primary no-padding-top no-padding-bottom" buttonStyle="link" onClick={handleClearFn}>
          {t('Clear')}
        </Button>);
        }
        return addons;
    };
    const innerField = useCallback((editing, rolesField, { required, disabled: fieldDisabled }) => {
        forceUpdateFields(rolesField);
        if (organisationGroupSelectorLoading || orgGroupRolesSelectorLoading) {
            return <Spinner bottom className="mt-20" inline={false}/>;
        }
        const _selection = selection(rolesField);
        const _viewSelector = viewSelector(rolesField);
        return editing ? (<FormTreeModalSelector isFormField isOnOverlay adapter={{ ...adapter, renderNodeAddons }} label={_label} models={models} selection={_selection} viewSelector={_viewSelector} {...rolesField} disabled={fieldDisabled || rolesField.meta.submitting} hasClearButton={false} input={{
            ...rolesField.input,
            onChange: onChange(rolesField),
        }} maxVisibleNames={_viewSelector.length} required={required} selectionFilter={selectionFilter}>
          {options => (<RoleAssignmentSelector {...options} adapter={{ ...adapter, renderNodeAddons }} plugins={plugins}/>)}
        </FormTreeModalSelector>) : (<SubtreeValueView adapter={adapter} label={_label} maxVisibleNames={_viewSelector.length} models={models} selection={_selection} viewSelector={_viewSelector}/>);
    }, [
        renderNodeAddons,
        viewSelector,
        selection,
        onChange,
        organisationGroupSelectorLoading,
        orgGroupRolesSelectorLoading,
        models,
        forceUpdateFields,
        _label,
    ]);
    return (<EntityField disabled={disabled} name={name} required={required} {...rest}>
      {innerField}
    </EntityField>);
};
const isGroup = ({ __typename }) => __typename === GROUP;
export const isOrganisation = node => (node === null || node === void 0 ? void 0 : node.__typename) === ORGANISATION;
export const isOrganisationStructure = ({ __typename }) => __typename === ORGANISATION_STRUCTURE;
export const isLevel = ({ __typename }) => __typename === LEVEL;
const isRole = ({ __typename }) => __typename === ROLE;
const isDefaultRole = ({ __typename, itemType }) => __typename === ROLE && itemType === 'ROLE_DEFAULT';
const nodeIsLeaf = node => isRole(node);
const resolveNodeParentId = node => {
    if (isGroup(node)) {
        return null;
    }
    if (isOrganisation(node)) {
        return `${GROUP}-${node.orgGroupId}`;
    }
    if (isOrganisationStructure(node)) {
        return `${ORGANISATION}-${node.organisationId}`;
    }
    if (isLevel(node) && node.parentId === null) {
        return `${ORGANISATION_STRUCTURE}-${node.organisationId}-${node.organisationStructureId}`;
    }
    if (isLevel(node) && node.parentId) {
        return `${LEVEL}-${node.organisationId}-${node.parentId}`;
    }
    if (isDefaultRole(node) &&
        node.organisationId &&
        node.organisationStructureId) {
        return `${ORGANISATION_STRUCTURE}-${node.organisationId}-${node.organisationStructureId}`;
    }
    if (isRole(node) && node.orgStructureLevelId) {
        return `${LEVEL}-${node.organisationId}-${node.orgStructureLevelId}`;
    }
    return node.parentId;
};
const resolveNodeId = node => {
    if (isGroup(node)) {
        return `${GROUP}-${node.id}`;
    }
    if (isOrganisation(node)) {
        return `${ORGANISATION}-${node.id}`;
    }
    if (isOrganisationStructure(node)) {
        return `${ORGANISATION_STRUCTURE}-${node.organisationId}-${node.id}`;
    }
    if (isLevel(node)) {
        return `${LEVEL}-${node.organisationId}-${node.id}`;
    }
    if (isRole(node)) {
        return `${ROLE}-${node.organisationId}-${node.id}`;
    }
    return node.id;
};
const resolveNodeClassName = node => {
    switch (true) {
        case isGroup(node) || isBusinessEntity(node):
            return 'jstree-node-bold';
        case isDefaultRole(node):
            return 'jstree-node-italic jstree-node-disabled';
        case isRole(node):
            return 'jstree-node-italic';
        default:
            return '';
    }
};
export const adapter = {
    nodeIsLeaf,
    resolveNodeParentId,
    resolveNodeId,
    resolveNodeClassName,
};
export default RoleAssignmentField;
